import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { postBillRefundInPerson, postBillRefund, postFoiNotValidRequest } from '../../dataClient/apiCalls'
import { getPaidApplicationFeeBills } from '../../util/billFormatting'
import { StyledDialog, FlexDialogContent, FlexColumn, FlexEndContainer } from '../styles/Containers'
import Button, { SecondaryButton } from '../common/Button'
import BodyText, { BoldFormTitle, BoldSpan, MedBodyText } from '../styles/Text'
import { Checkbox, FormControlLabel } from '../styles/Checkbox'
import { ErrorMessage, WarningMessage } from '../common/UserMessage'

async function refundPaidApplicationFees(bills) {
  const paidFees = getPaidApplicationFeeBills(bills)
  for (const bill of paidFees) {
    if (bill.paymentMethod === 'IN_PERSON') {
      await postBillRefundInPerson(bill.id)
    } else {
      await postBillRefund(bill.id)
    }
  }
}

function NotAValidRequestButton({ foiId, bills = [], onSuccess = () => {} }) {
  const [open, setOpen] = useState(false)
  const [refundApplicationFee, setRefundApplicationFee] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const [loading, setLoading] = useState(false)

  const paidApplicationFees = getPaidApplicationFeeBills(bills)
  const hasPaidApplicationFee = paidApplicationFees.length > 0

  useEffect(() => {
    if (!open) return
    setSubmitError(false)
    setRefundApplicationFee(false)
  }, [open])

  const handleOpen = () => {
    setOpen(true)
    setSubmitError(false)
  }

  const handleClose = () => {
    if (loading) return
    setOpen(false)
    setSubmitError(false)
    setRefundApplicationFee(false)
  }

  const handleRemove = async () => {
    setLoading(true)
    setSubmitError(false)

    try {
      if (hasPaidApplicationFee && refundApplicationFee) {
        await refundPaidApplicationFees(bills)
      }
      await postFoiNotValidRequest(foiId)
      setOpen(false)
      onSuccess()
    } catch (e) {
      console.error('Failed to remove invalid FOI', foiId, e)
      setSubmitError(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <SecondaryButton aria-label="Not a Valid Request" onClick={handleOpen}>
        Not a Valid Request
      </SecondaryButton>
      <StyledDialog fullWidth open={open} onClose={handleClose} keepMounted={false} maxwidth="560px">
        <FlexDialogContent>
          <FlexColumn gap="16px">
            <BoldFormTitle>Remove Invalid Request</BoldFormTitle>

            {hasPaidApplicationFee ? (
              <BodyText>
                Removing this invalid request will remove it from the system and you will no longer be able to see it in your FOI list. You may choose
                to refund the application fee while removing the request by checking the box below.
              </BodyText>
            ) : (
              <BodyText>
                Removing this invalid request will remove it from the system and you will no longer be able to see it in your FOI list.
              </BodyText>
            )}

            {hasPaidApplicationFee && (
              <FormControlLabel
                control={<Checkbox checked={refundApplicationFee} onChange={e => setRefundApplicationFee(e.target.checked)} disabled={loading} />}
                label="Refund application fee."
              />
            )}

            {hasPaidApplicationFee ? (
              <WarningMessage>
                <MedBodyText>
                  This action is irreversible. If you choose not to refund the application fee, you will <BoldSpan>not</BoldSpan> be able to refund it
                  later.
                </MedBodyText>
              </WarningMessage>
            ) : (
              <WarningMessage>
                <MedBodyText>This action is irreversible.</MedBodyText>
              </WarningMessage>
            )}

            {submitError && <ErrorMessage>There was an error removing the request. Please try again.</ErrorMessage>}

            <FlexEndContainer>
              <SecondaryButton onClick={handleClose} disabled={loading}>
                Cancel
              </SecondaryButton>
              <Button onClick={handleRemove} loading={loading}>
                Remove
              </Button>
            </FlexEndContainer>
          </FlexColumn>
        </FlexDialogContent>
      </StyledDialog>
    </>
  )
}

NotAValidRequestButton.propTypes = {
  foiId: PropTypes.string.isRequired,
  bills: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['APPLICATION_FEE', 'FEE_ESTIMATE', 'INVOICE']).isRequired,
      status: PropTypes.string.isRequired,
      paymentMethod: PropTypes.oneOf(['ONLINE', 'IN_PERSON'])
    })
  ),
  onSuccess: PropTypes.func
}

export default NotAValidRequestButton
